// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Custom hook for managing asset transition data fetching and state.
 *
 * Handles data loading, error states, and provides computed values for
 * asset flow calculations for the inventory variant of the asset transition report.
 */
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { getInventoryRollforwardData } from '@/report/api/get-inventory-rollforward-data';
import { useRollforwardCurrencyQuery } from '@/report/hooks/use-rollforward-currency-query';
import { DateTime } from 'luxon';

interface UseAssetRollforwardDataParams {
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
  activeCurrencyCode: IsoCurrencyCode;
}

export function useInventoryRollforwardData(
  params: UseAssetRollforwardDataParams
) {
  const startDateTime = params.startDate
    ? DateTime.fromISO(params.startDate)
    : null;
  const endDateTime = params.endDate ? DateTime.fromISO(params.endDate) : null;

  const query = useRollforwardCurrencyQuery(
    'asset-rollforward-inventory',
    startDateTime,
    endDateTime,
    params.enabled !== false,
    params.activeCurrencyCode,
    getInventoryRollforwardData
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
